<template>
  <div class="w-full bg-gray-50 h-screen flex flex-col">
    <!-- Header -->
    <div class="bg-white border-b px-6 py-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <button @click="goBack" class="text-gray-600 hover:text-gray-800">
            <font-awesome-icon icon="arrow-left" class="mr-2" />
            Zurück zur Tabelle
          </button>
          <h1 class="text-2xl font-bold">
            {{ contactId ? `Kontakt #${contactId} bearbeiten` : 'Neuen Kontakt anlegen' }}
          </h1>
        </div>
        <div class="flex gap-2">
          <button v-if="contactId" @click="saveContact" :disabled="loading || !hasChanges"
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
            <font-awesome-icon icon="save" class="mr-2" />
            Speichern
          </button>
          <button @click="saveAndClose" :disabled="loading"
            class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
            <font-awesome-icon icon="save" class="mr-2" />
            Speichern und schließen
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-auto p-6">
      <div class="max-w-4xl mx-auto">
        <div class="bg-white rounded-lg shadow p-6">
          <h2 class="text-xl font-semibold mb-6">Kontaktdaten</h2>

          <!-- Contact ID -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">ID</label>
              <div class="p-2 bg-gray-50 rounded">
                {{ contactId || 'Wird beim Speichern erzeugt' }}
              </div>
            </div>
          </div>

          <!-- Form Fields -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Personal Data -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Anrede</label>
              <select v-model="contact.Anrede" class="w-full border rounded px-3 py-2">
                <option value="Bitte Auswählen">Bitte Auswählen</option>
                <option value="Herr">Herr</option>
                <option value="Frau">Frau</option>
                <option value="Sonstige">Sonstige</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Nachname</label>
              <input v-model="contact.Nachname" type="text" class="w-full border rounded px-3 py-2" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Vorname</label>
              <input v-model="contact.Vorname" type="text" class="w-full border rounded px-3 py-2" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Unternehmen</label>
              <input v-model="contact.Unternehmen" type="text" class="w-full border rounded px-3 py-2" />
            </div>

            <!-- Address -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Straße</label>
              <input v-model="contact.Strasse" type="text" class="w-full border rounded px-3 py-2" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Hausnummer</label>
              <input v-model="contact.Hausnummer" type="text" class="w-full border rounded px-3 py-2" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Postleitzahl</label>
              <input v-model="contact.Postleitzahl" type="text" class="w-full border rounded px-3 py-2" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Ort</label>
              <input v-model="contact.Ort" type="text" class="w-full border rounded px-3 py-2" />
            </div>

            <!-- Contact Information -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Festnetz</label>
              <input v-model="contact.Festnetz" type="text" class="w-full border rounded px-3 py-2" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Mobilnummer</label>
              <input v-model="contact.Mobilnummer" type="text" class="w-full border rounded px-3 py-2" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">E-Mail 1</label>
              <input v-model="contact.Email_1" type="email" class="w-full border rounded px-3 py-2" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">E-Mail 2</label>
              <input v-model="contact.Email_2" type="email" class="w-full border rounded px-3 py-2" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Fax</label>
              <input v-model="contact.Fax" type="text" class="w-full border rounded px-3 py-2" />
            </div>

            <!-- Additional Information -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Funktion</label>
              <input v-model="contact.Funktion" type="text" class="w-full border rounded px-3 py-2" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Geburtsdatum</label>
              <input v-model="contact.Geburtsdatum" type="date" class="w-full border rounded px-3 py-2" />
            </div>

            <div class="flex items-center pt-6">
              <input v-model="contact.Weihnachtskarte" type="checkbox" class="small" id="weihnachtskarte" />
              <label for="weihnachtskarte">Weihnachtskarte</label>
            </div>

            <!-- Sonstiges (full width) -->
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">Sonstiges</label>
              <textarea v-model="contact.Sonstiges" rows="3" class="w-full border rounded px-3 py-2"></textarea>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6">
        <div class="text-lg font-semibold">Lade...</div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@/API'

export default {
  name: 'ContactEdit',
  props: {
    user: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      contactId: null,
      originalContact: {},
      contact: {
        id: null,
        Anrede: 'Bitte Auswählen',
        Nachname: '',
        Vorname: '',
        Unternehmen: '',
        Strasse: '',
        Hausnummer: '',
        Postleitzahl: '',
        Ort: '',
        Festnetz: '',
        Mobilnummer: '',
        Email_1: '',
        Email_2: '',
        Fax: '',
        Funktion: '',
        Weihnachtskarte: true,
        Geburtsdatum: '',
        Sonstiges: ''
      }
    }
  },
  computed: {
    hasChanges() {
      return JSON.stringify(this.contact) !== JSON.stringify(this.originalContact)
    },
    baseOrigin() {
      try {
        const envUrl = process.env.VUE_APP_INTERNAL_URL
        if (envUrl) {
          return new URL(envUrl).origin
        }
      } catch (e) { }
      return new URL(window.location.href).origin
    }
  },
  async created() {
    const id = this.$route.params.id || this.$route.query.id
    if (id && id !== 'new') {
      this.contactId = parseInt(id)
      await this.loadContact()
    }
  },
  methods: {
    async loadContact() {
      if (!this.contactId) return

      this.loading = true
      try {
        const response = await new Promise((resolve, reject) => {
          API.getContact(this.contactId, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error(`Failed to load contact: ${status}`))
            }
          })
        })

        this.contact = { ...this.contact, ...response }
        this.originalContact = JSON.parse(JSON.stringify(this.contact))

      } catch (error) {
        console.error('Error loading contact:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Laden des Kontakts',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.loading = false
      }
    },

    async saveContact() {
      this.saving = true
      try {
        const contactData = { ...this.contact }

        // Convert empty date strings to null
        if (contactData.Geburtsdatum === '') {
          contactData.Geburtsdatum = null
        }

        const response = await new Promise((resolve, reject) => {
          if (this.contactId) {
            API.updateContact(this.contactId, contactData, (data, status) => {
              if (status === 200) {
                resolve(data)
              } else {
                reject(new Error('Failed to update contact'))
              }
            })
          } else {
            API.createContact(contactData, (data, status) => {
              if (status === 201) {
                resolve(data)
              } else {
                reject(new Error('Failed to create contact'))
              }
            })
          }
        })

        this.contact = { ...this.contact, ...response }
        this.originalContact = JSON.parse(JSON.stringify(this.contact))

        if (!this.contactId) {
          this.contactId = response.id
          this.$router.replace({ query: { ...this.$route.query, id: this.contactId } })
        }

        this.$toast?.open?.({
          message: 'Kontakt gespeichert',
          type: 'success',
          position: 'top'
        })
        return true

      } catch (error) {
        console.error('Error saving contact:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Speichern',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.saving = false
      }
      return false
    },

    async saveAndClose() {
      if (await this.saveContact()) {
        this.goBack()
      }
    },

    goBack() {
      const referrer = document.cookie.split('; ').find(row => row.startsWith('tableReferrer='))
      if (referrer) {
        const url = referrer.split('=')[1]
        window.location.href = decodeURIComponent(url)
      } else {
        this.$router.push('/contacts')
      }
    }
  }
}
</script>

<style scoped>
</style>
